import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

export interface TagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
}

function normalizeTag(raw: string) {
  // trim + collapse internal whitespace
  return raw.trim().replace(/\s+/g, ' ');
}

function dedupeCaseInsensitive(tags: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tags) {
    const key = t.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(t);
    }
  }
  return out;
}

function parseTags(input: string): string[] {
  // Split by comma, then normalize each tag
  return input
    .split(',')
    .map((tag) => normalizeTag(tag))
    .filter(Boolean);
}

export function TagsInput({
  value,
  onChange,
  label = 'Tags',
  placeholder = 'Type and press enter/comma to separate',
  disabled,
  error,
  helperText,
  required,
  fullWidth,
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    // Check if the input ends with a comma
    if (newInputValue.endsWith(',') && newInputValue.trim().length > 1) {
      // Parse tags from the input (everything before the comma)
      const tagsToAdd = parseTags(newInputValue.slice(0, -1));
      if (tagsToAdd.length > 0) {
        // Add new tags to existing value
        const newTags = dedupeCaseInsensitive([...value, ...tagsToAdd]);
        onChange(newTags);
        // Clear input after adding tags
        setInputValue('');
        return;
      }
    }
    // Otherwise, just update the input value
    setInputValue(newInputValue);
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: (string | { inputValue: string } | string[])[],
  ) => {
    const normalized = newValue.map((v) => normalizeTag(String(v))).filter(Boolean);
    onChange(dedupeCaseInsensitive(normalized));
    // Clear input after adding via Enter
    setInputValue('');
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // If there's content in the input, add it as tags
    if (inputValue.trim()) {
      const tagsToAdd = parseTags(inputValue);
      if (tagsToAdd.length > 0) {
        const newTags = dedupeCaseInsensitive([...value, ...tagsToAdd]);
        onChange(newTags);
        setInputValue('');
      }
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      disableClearable
      options={[]} // no predefined options; fully arbitrary
      value={value}
      inputValue={inputValue}
      disabled={disabled}
      filterSelectedOptions
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
            key={`${option}-${index}`}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          fullWidth={fullWidth}
        />
      )}
    />
  );
}
